/**
 * BYOK API-key CRUD mutation hooks for the AC-1493 BYOK epic.
 *
 * AC-1515 (Phase 7B). Companion to `useApiKeys()` (read).
 *
 * ## Endpoints
 *
 *   - `POST   /api/v1/credentials/{provider}`      — add (backend validates first; 400 if invalid, 409 if duplicate)
 *   - `PUT    /api/v1/credentials/{provider}`      — replace
 *   - `DELETE /api/v1/credentials/{provider}`      — remove
 *   - `POST   /api/v1/credentials/{provider}/test` — re-test stored key
 *
 * ## Invalidation contract
 *
 * Every mutation invalidates BOTH:
 *
 *   - `queryKeys.apiKeys()`   — the user's key list. Any `useApiKeys()`
 *                              consumer (the settings page, the agent
 *                              picker, the orchestration banner) refreshes
 *                              its `has_key` view automatically.
 *
 *   - `queryKeys.llmCatalog()` — because the server-side `available`
 *                                flag on each catalog entry is derived
 *                                from whether the user has a key for
 *                                that provider (BYOK mode). After
 *                                add/replace/delete, the picker's
 *                                disabled-row state must update.
 *
 * `useTestApiKey` also invalidates `apiKeys()` because the server may
 * update `last_validated_at` (and toggle a transient `has_key` flag if
 * the stored key has rotated server-side).
 *
 * All mutations are pessimistic — no optimistic update. The form UX
 * exposes pending state via `mutation.isPending`, and on `error` the
 * call site renders the server's error payload (4xx validation, 409
 * duplicate). Same pattern as `useAgentMutations` / `useModelMutations`.
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import {
  ApiKeyTestResult,
  CreateApiKeyRequest,
  LLMProvider,
  UserApiKey,
} from '../../types';
import { queryKeys } from './queryKeys';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Invalidate the apiKeys list AND the llm catalog. Extracted so all four
 * mutations stay in sync if the invalidation contract grows.
 */
function invalidateApiKeyDerivedKeys(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
  queryClient.invalidateQueries({ queryKey: queryKeys.llmCatalog() });
}

// ---------------------------------------------------------------------------
// Mutation variable shapes
// ---------------------------------------------------------------------------

export type CreateApiKeyVariables = {
  provider: LLMProvider;
  data: CreateApiKeyRequest;
};

export type UpdateApiKeyVariables = {
  provider: LLMProvider;
  data: CreateApiKeyRequest;
};

export type DeleteApiKeyVariables = {
  provider: LLMProvider;
};

export type TestApiKeyVariables = {
  provider: LLMProvider;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * `useCreateApiKey()` — `POST /api/v1/credentials/{provider}`.
 *
 * Returns the updated `UserApiKey` row. The backend validates the supplied
 * key against the provider's API before persisting; on failure the request
 * rejects with a 400 (invalid key) or 409 (already configured), which RQ
 * surfaces via `mutation.error`.
 */
export function useCreateApiKey(): UseMutationResult<
  UserApiKey,
  Error,
  CreateApiKeyVariables
> {
  const queryClient = useQueryClient();
  return useMutation<UserApiKey, Error, CreateApiKeyVariables>({
    mutationFn: ({ provider, data }) => apiService.createApiKey(provider, data),
    onSuccess: () => {
      invalidateApiKeyDerivedKeys(queryClient);
    },
  });
}

/**
 * `useUpdateApiKey()` — `PUT /api/v1/credentials/{provider}`.
 *
 * Replaces the stored key. Same validation behavior as create.
 */
export function useUpdateApiKey(): UseMutationResult<
  UserApiKey,
  Error,
  UpdateApiKeyVariables
> {
  const queryClient = useQueryClient();
  return useMutation<UserApiKey, Error, UpdateApiKeyVariables>({
    mutationFn: ({ provider, data }) => apiService.updateApiKey(provider, data),
    onSuccess: () => {
      invalidateApiKeyDerivedKeys(queryClient);
    },
  });
}

/**
 * `useDeleteApiKey()` — `DELETE /api/v1/credentials/{provider}`.
 *
 * Removes the stored key. Invalidates the list AND the llm catalog because
 * affected models flip to unavailable in BYOK mode.
 */
export function useDeleteApiKey(): UseMutationResult<
  void,
  Error,
  DeleteApiKeyVariables
> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteApiKeyVariables>({
    mutationFn: ({ provider }) => apiService.deleteApiKey(provider),
    onSuccess: () => {
      invalidateApiKeyDerivedKeys(queryClient);
    },
  });
}

/**
 * `useTestApiKey()` — `POST /api/v1/credentials/{provider}/test`.
 *
 * Re-tests the stored key against the provider. Returns the test result
 * payload. Invalidates `apiKeys()` because the server may update
 * `last_validated_at` (and surface a key-rotation event). Does NOT
 * invalidate `llmCatalog()` — a passing or failing test does not change
 * which models are "available"; that's a function of whether the user has
 * the key, not whether the key currently authenticates.
 */
export function useTestApiKey(): UseMutationResult<
  ApiKeyTestResult,
  Error,
  TestApiKeyVariables
> {
  const queryClient = useQueryClient();
  return useMutation<ApiKeyTestResult, Error, TestApiKeyVariables>({
    mutationFn: ({ provider }) => apiService.testApiKey(provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
    },
  });
}
